import { InputValue, NodeParam, NodeStruct } from '@/types/node-types';

/**
 * Decision Preset authoring sugar for the Classify / Decide node.
 *
 * The backend `decision_preset` param is inert runtime metadata (the node's
 * behavior is fully defined by decision_labels/criteria). Picking a preset in
 * the builder UI applies label/criteria scaffolds via the functions below;
 * picking `custom` changes nothing.
 */

export const CLASSIFY_DECIDE_NODE_ID = 'classify_decide_node';
export const DECISION_PRESET_FIELD_ID = 'decision_preset';

export interface DecisionPresetScaffold {
  labels: string[];
  criteriaTemplate: string;
}

export const DECISION_PRESET_SCAFFOLDS: Record<string, DecisionPresetScaffold> =
  {
    binary_gate: {
      labels: ['match', 'no_match'],
      criteriaTemplate:
        'Decide whether the subject satisfies the requirements below. ' +
        "Choose 'match' only when the supplied evidence clearly supports " +
        "every requirement; otherwise choose 'no_match'.\n\nRequirements:\n- ",
    },
    triage: {
      labels: ['urgent', 'routine', 'ignore'],
      criteriaTemplate:
        'Triage the subject into exactly one bucket. ' +
        "Choose 'urgent' when it needs prompt human attention, 'routine' " +
        "when it can wait for normal processing, and 'ignore' when it is " +
        'irrelevant or spam.\n\nTriage rules:\n- ',
    },
  };

export interface PresetFieldUpdate {
  fieldId: string;
  value: InputValue;
}

/** True when this field is the Decision Preset dropdown on a Classify node. */
export function isClassifyDecisionPresetField(
  struct: Pick<NodeStruct, 'node_id'> | undefined,
  field: Pick<NodeParam, 'field_id'>,
): boolean {
  return (
    struct?.node_id === CLASSIFY_DECIDE_NODE_ID &&
    field.field_id === DECISION_PRESET_FIELD_ID
  );
}

const isBlank = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

/** True when the current criteria is empty or still a known preset template
 * (i.e. not hand-written), so a newly picked preset may replace it. */
const criteriaIsReplaceable = (criteria: unknown): boolean => {
  if (isBlank(criteria)) return true;
  const text = String(criteria).trim();
  return Object.values(DECISION_PRESET_SCAFFOLDS).some(
    (scaffold) => scaffold.criteriaTemplate.trim() === text,
  );
};

/**
 * Compute the param updates a preset selection should apply.
 *
 * - `custom` (or an unknown value) is a no-op.
 * - `decision_labels` is always set to the preset's label set.
 * - `criteria` is scaffolded only when the current criteria is blank or still
 *   equal to another preset's template — hand-written criteria is never
 *   clobbered.
 */
export function getDecisionPresetUpdates(
  preset: string,
  current: { decisionLabels?: unknown; criteria?: unknown },
): PresetFieldUpdate[] {
  const scaffold = DECISION_PRESET_SCAFFOLDS[preset];
  if (!scaffold) return [];

  const updates: PresetFieldUpdate[] = [
    { fieldId: 'decision_labels', value: [...scaffold.labels] },
  ];
  if (criteriaIsReplaceable(current.criteria)) {
    updates.push({ fieldId: 'criteria', value: scaffold.criteriaTemplate });
  }
  return updates;
}

/** Convenience: read current labels/criteria off the node struct and compute
 * the updates for a preset selection. */
export function getDecisionPresetUpdatesForStruct(
  preset: string,
  struct: Pick<NodeStruct, 'params'> | undefined,
): PresetFieldUpdate[] {
  const params = struct?.params ?? [];
  const paramValue = (fieldId: string): unknown =>
    params.find((param) => param.field_id === fieldId)?.value;
  return getDecisionPresetUpdates(preset, {
    decisionLabels: paramValue('decision_labels'),
    criteria: paramValue('criteria'),
  });
}
